import { type ErrorComponentProps, useRouter } from '@tanstack/react-router';
import { useEffect } from 'react';
import { Button } from '@/components/ui/button';

/** Safari says "Load failed", Chrome "Failed to fetch", Firefox "NetworkError…" */
function isNetworkError(error: unknown) {
  return (
    error instanceof TypeError &&
    /load failed|failed to fetch|networkerror|network connection/i.test(error.message)
  );
}

/** Router-level error screen with a retry, instead of TanStack's bare default. */
export function RouteError({ error, reset }: ErrorComponentProps) {
  const router = useRouter();
  const offline = isNetworkError(error);

  const retry = () => {
    reset();
    void router.invalidate();
  };

  // Retry by itself when the connection comes back
  // biome-ignore lint/correctness/useExhaustiveDependencies: retry is recreated every render
  useEffect(() => {
    if (!offline) return;
    window.addEventListener('online', retry);
    return () => window.removeEventListener('online', retry);
  }, [offline]);

  useEffect(() => {
    console.error('Route error:', error);
  }, [error]);

  return (
    <div className="flex min-h-dvh items-center justify-center bg-background px-4">
      <div className="w-full max-w-sm rounded-2xl border border-border bg-card p-6 text-center">
        <span
          aria-hidden
          className="mx-auto flex size-12 items-center justify-center rounded-full bg-negative-soft text-xl text-negative"
        >
          !
        </span>
        <h1 className="mt-3 text-lg font-semibold tracking-tight">
          {offline ? 'Ingen anslutning' : 'Något gick fel'}
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {offline
            ? 'Budgie kunde inte nå servern. Kontrollera uppkopplingen och försök igen.'
            : 'Sidan kunde inte visas. Försök igen, eller ladda om appen om det inte hjälper.'}
        </p>
        <Button type="button" onClick={retry} className="mt-5 w-full">
          Försök igen
        </Button>
        <Button
          type="button"
          variant="ghost"
          onClick={() => window.location.reload()}
          className="mt-2 w-full"
        >
          Ladda om appen
        </Button>
      </div>
    </div>
  );
}
